import React, { useState } from "react";
import * as finService from "../../services/finService";
import useGlContext, { fmtMoney } from "./gl/glContext";
import { JournalEditor } from "./FinJournals";
import { DataTable, GlState, LedgerBar, accountTypeLabel, downloadCsv, hint, money, num } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, cardStyle, formatDate, inputStyle, pageStyle, tdStyle, todayYmd, useLoad } from "../../components/ui";

// General ledger reports, all read from posted balances: trial balance, profit and loss,
// balance sheet, the postings to one account, and the journal register of a period.
const firstOfMonth = () => `${todayYmd().slice(0, 8)}01`;

function usePeriodPicker({ gl, value, onChange }) {
  const { data } = useLoad(() => finService.listPeriods(gl.ledger.id), [gl.ledger.code]);
  const opts = (data || []).filter((p) => p.statuses.GL !== "NEVER_OPENED").map((p) => ({ label: p.period_name, value: p.period_name, id: p.id }));
  const chosen = opts.find((o) => o.value === value) || opts.filter((o) => !/^ADJ/.test(o.value)).slice(-1)[0] || null;
  return { chosen, control: <AppDropdown id="rpt-period" value={chosen ? chosen.value : null} options={opts} onChange={(e) => onChange(e.value)} placeholder="Period" filter={opts.length > 8} /> };
}

function TrialBalance({ gl }) {
  const [period, setPeriod] = useState(null);
  const pick = usePeriodPicker({ gl, value: period, onChange: setPeriod });
  const periodId = pick.chosen ? pick.chosen.id : null;
  const { data, loading, error, reload } = useLoad(() => finService.trialBalance(gl.ledger.id, periodId), [gl.ledger.code, pick.chosen && pick.chosen.value], { enabled: !!periodId });
  const rows = data ? data.rows : [];
  const csv = () =>
    downloadCsv(
      `trial-balance-${pick.chosen.value}.csv`,
      ["Account", "Description", "Type", "Opening", "Debits", "Credits", "Closing"],
      rows.map((r) => [r.account, r.account_description, accountTypeLabel(r.account_type), r.begin, r.period_dr, r.period_cr, r.end])
    );
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div style={{ width: 180 }}>{pick.control}</div>
        {data && rows.length ? <SecondaryButton onClick={csv}>Download CSV</SecondaryButton> : null}
        {data ? <span style={{ ...hint, color: data.balanced ? "#065F46" : "#991B1B", fontWeight: 600 }}>{data.balanced ? "In balance" : "Out of balance"}</span> : null}
      </div>
      {!periodId ? (
        <EmptyState title="No open periods yet" />
      ) : (
        <LoadState loading={loading} error={error} onRetry={reload}>
          <DataTable
            rows={rows}
            rowKey={(r, i) => `${r.account}-${i}`}
            minWidth={860}
            empty={<EmptyState title="No balances" text="Nothing has been posted up to this period." />}
            columns={[
              { label: "Account", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{r.account}</span> },
              { label: "Description", render: (r) => r.account_description },
              { label: "Opening", num: true, render: (r) => fmtMoney(r.begin) },
              { label: "Debits", num: true, render: (r) => money(r.period_dr) },
              { label: "Credits", num: true, render: (r) => money(r.period_cr) },
              { label: "Closing", num: true, render: (r) => fmtMoney(r.end) },
            ]}
            footer={
              data ? (
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Total ({data.ledger.currency})
                  </td>
                  <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(data.totals.begin)}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(data.totals.period_dr)}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(data.totals.period_cr)}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(data.totals.end)}</td>
                </tr>
              ) : null
            }
          />
        </LoadState>
      )}
    </>
  );
}

function Statement({ gl, report }) {
  const [period, setPeriod] = useState(null);
  const pick = usePeriodPicker({ gl, value: period, onChange: setPeriod });
  const periodId = pick.chosen ? pick.chosen.id : null;
  const { data, loading, error, reload } = useLoad(() => finService.statement(gl.ledger.id, periodId, report), [gl.ledger.code, report, pick.chosen && pick.chosen.value], { enabled: !!periodId });
  const rows = data ? data.rows : [];
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div style={{ width: 180 }}>{pick.control}</div>
        {rows.length ? (
          <SecondaryButton onClick={() => downloadCsv(`${report.toLowerCase()}-${pick.chosen.value}.csv`, ["Line", "Amount"], rows.map((r) => [r.label, r.amount === null ? "" : r.amount]))}>Download CSV</SecondaryButton>
        ) : null}
        {report === "PL" ? <span style={hint}>Year to date, to the end of the period.</span> : <span style={hint}>As at the end of the period.</span>}
      </div>
      {!periodId ? (
        <EmptyState title="No open periods yet" />
      ) : (
        <LoadState loading={loading} error={error} onRetry={reload}>
          <div style={{ ...cardStyle, maxWidth: 720 }}>
            <table className="table mb-0">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.seq}>
                    <td style={{ ...tdStyle, fontWeight: r.bold ? 700 : 400, paddingLeft: r.row_type === "HEADING" || r.bold ? 16 : 32 }}>{r.label}</td>
                    <td style={{ ...num, fontWeight: r.bold ? 700 : 400 }}>{r.amount === null ? "" : fmtMoney(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data ? <div style={{ ...hint, marginTop: 8 }}>Amounts in {data.ledger.currency}.</div> : null}
        </LoadState>
      )}
    </>
  );
}

function AccountDetail({ gl, onOpenJournal }) {
  const [q, setQ] = useState({ account: "", date_from: firstOfMonth(), date_to: todayYmd() });
  const [run, setRun] = useState(null);
  const { data, loading, error, reload } = useLoad(() => finService.accountDetail({ ledger_id: gl.ledger.id, ...run }), [gl.ledger.code, run && JSON.stringify(run)], { enabled: !!run });
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <Field label="Account (full combination)" htmlFor="ad-acct" style={{ minWidth: 320 }}>
          <input id="ad-acct" className="form-control" style={{ ...inputStyle, fontFamily: "monospace" }} placeholder={gl.structure.segments.map((s) => s.default_value || s.qualifier.slice(0, 2)).join(gl.structure.delimiter)} value={q.account} onChange={(e) => setQ({ ...q, account: e.target.value.trim().toUpperCase() })} />
        </Field>
        <Field label="From" htmlFor="ad-from">
          <input id="ad-from" type="date" className="form-control" style={inputStyle} value={q.date_from} onChange={(e) => setQ({ ...q, date_from: e.target.value })} />
        </Field>
        <Field label="To" htmlFor="ad-to">
          <input id="ad-to" type="date" className="form-control" style={inputStyle} value={q.date_to} onChange={(e) => setQ({ ...q, date_to: e.target.value })} />
        </Field>
        <PrimaryButton style={{ marginBottom: 16 }} disabled={!q.account} onClick={() => setRun({ ...q })}>
          Show
        </PrimaryButton>
      </div>
      {run ? (
        <LoadState loading={loading} error={error} onRetry={reload}>
          {data ? (
            <>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>{data.account}</strong> {data.account_description} · opening {fmtMoney(data.opening)} · closing {fmtMoney(data.closing)} {data.currency}
              </div>
              <DataTable
                rows={data.lines}
                rowKey={(r) => `${r.je_number}-${r.line_no}`}
                onRowClick={(r) => onOpenJournal(r.journal_id)}
                minWidth={860}
                empty={<EmptyState title="No postings in these dates" />}
                columns={[
                  { label: "Date", render: (r) => formatDate(r.accounting_date) },
                  { label: "Journal", render: (r) => r.je_number },
                  { label: "Name", render: (r) => r.description || r.name },
                  { label: "Source", render: (r) => r.source_code },
                  { label: "Debit", num: true, render: (r) => money(r.accounted_dr) },
                  { label: "Credit", num: true, render: (r) => money(r.accounted_cr) },
                  { label: "Balance", num: true, render: (r) => fmtMoney(r.balance) },
                ]}
              />
              {data.lines.length ? (
                <div className="mt-2">
                  <SecondaryButton
                    onClick={() =>
                      downloadCsv(`account-${data.account}.csv`, ["Date", "Journal", "Name", "Source", "Currency", "Entered Dr", "Entered Cr", "Debit", "Credit", "Balance"], data.lines.map((r) => [r.accounting_date, r.je_number, r.description || r.name, r.source_code, r.currency, r.entered_dr, r.entered_cr, r.accounted_dr, r.accounted_cr, r.balance]))
                    }
                  >
                    Download CSV
                  </SecondaryButton>
                </div>
              ) : null}
            </>
          ) : null}
        </LoadState>
      ) : (
        <div style={hint}>Enter an account and dates.</div>
      )}
    </>
  );
}

function Register({ gl, onOpenJournal }) {
  const [period, setPeriod] = useState(null);
  const pick = usePeriodPicker({ gl, value: period, onChange: setPeriod });
  const periodId = pick.chosen ? pick.chosen.id : null;
  const { data, loading, error, reload } = useLoad(() => finService.listJournals({ ledger_id: gl.ledger.id, period_id: periodId, status: "POSTED", size: 200 }), [gl.ledger.code, pick.chosen && pick.chosen.value], { enabled: !!periodId });
  const rows = data || [];
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div style={{ width: 180 }}>{pick.control}</div>
        {rows.length ? <SecondaryButton onClick={() => downloadCsv(`journal-register-${pick.chosen.value}.csv`, ["Number", "Date", "Name", "Source", "Category", "Currency", "Amount", "Accounted"], rows.map((r) => [r.je_number, r.accounting_date, r.name, r.source_code, r.category_code, r.currency, r.total_entered_dr, r.total_accounted_dr]))}>Download CSV</SecondaryButton> : null}
        {rows.length === 200 ? <span style={hint}>First 200 journals shown; use Journals to search further.</span> : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => onOpenJournal(r.id)}
          empty={<EmptyState title="No posted journals in this period" />}
          columns={[
            { label: "Number", render: (r) => r.je_number },
            { label: "Date", render: (r) => formatDate(r.accounting_date) },
            { label: "Name", render: (r) => r.name },
            { label: "Source", render: (r) => r.source_code },
            { label: "Category", render: (r) => r.category_code },
            { label: "Amount", num: true, render: (r) => `${fmtMoney(r.total_entered_dr)} ${r.currency}` },
            { label: `Accounted (${gl.ledger.currency})`, num: true, render: (r) => fmtMoney(r.total_accounted_dr) },
          ]}
        />
      </LoadState>
    </>
  );
}

export default function FinReports() {
  const gl = useGlContext();
  const [tab, setTab] = useState("tb");
  const [journal, setJournal] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Financial Reports" subtitle="Trial balance, statements and account detail from the general ledger" />
      <GlState gl={gl}>
        <LedgerBar gl={gl} />
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "tb", label: "Trial balance" },
            { key: "pl", label: "Profit and loss" },
            { key: "bs", label: "Balance sheet" },
            { key: "detail", label: "Account detail" },
            { key: "register", label: "Journal register" },
          ]}
        />
        <div key={gl.ledger && gl.ledger.code}>
          {tab === "tb" ? <TrialBalance gl={gl} /> : null}
          {tab === "pl" ? <Statement gl={gl} report="PL" /> : null}
          {tab === "bs" ? <Statement gl={gl} report="BS" /> : null}
          {tab === "detail" ? <AccountDetail gl={gl} onOpenJournal={setJournal} /> : null}
          {tab === "register" ? <Register gl={gl} onOpenJournal={setJournal} /> : null}
        </div>
        {journal ? <JournalEditor gl={gl} journalId={journal} onHide={() => setJournal(null)} onChanged={() => {}} /> : null}
      </GlState>
    </div>
  );
}
